import React from 'react';
import { BrowserRouter, Routes, Route, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import ErrorBoundary from './components/ErrorBoundary';
import PageTransition from './components/PageTransition';
import AdminGate from './components/AdminGate';
import AnimatedBackground from './components/AnimatedBackground';
import Hero from './components/Hero';
import ActiveGames from './components/ActiveGames';
import GameCatalog from './components/GameCatalog';
import Events from './components/Events';
import AdminPanel from './components/AdminPanel';
import HowToJoin from './components/HowToJoin';
import NotFound from './components/NotFound';
import Footer from './components/Footer';
import { ToastProvider } from './components/Toast';
import AnnouncementBanner from './components/AnnouncementBanner';
import GolfCourseWidget from './components/GolfCourseWidget';
import ClubhouseDining from './components/ClubhouseDining';
import { PS2GraphicsProvider, usePS2Graphics } from './context/PS2GraphicsContext';
import PS2CrtOverlay from './components/PS2CrtOverlay';
import PS2BootAnimation from './components/PS2BootAnimation';
import './index.css';

import { CountryClubCrest, CrtMonitorIcon, Ps2ControllerIcon } from './components/Icons';

function Navigation() {
  const location = useLocation();
  const { crtFilter, toggleCrtFilter, ps2Mode, togglePs2Mode, playPs2Click } = usePS2Graphics();

  // Don't show nav on admin pages
  if (location.pathname.startsWith('/admin')) return null;

  return (
    <nav className={`backdrop-blur-xl border-b transition-colors ${
      ps2Mode ? 'bg-slate-950/85 border-cyan-500/30' : 'bg-black/80 border-white/[0.06]'
    }`}>
      <div className="max-w-5xl mx-auto px-4 md:px-6 flex items-center justify-between h-11">
        <div className="flex items-center gap-3">
          <NavLink
            to="/"
            onClick={playPs2Click}
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
          >
            <CountryClubCrest className={`w-5 h-5 ${ps2Mode ? 'text-cyan-400' : 'text-emerald-400'}`} />
            <span className="text-xs font-bold font-mono tracking-tight hidden sm:block">
              The Country<span className={ps2Mode ? 'text-cyan-400' : 'text-emerald-400'}>Club</span>
            </span>
          </NavLink>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2">
          <NavLink
            to="/"
            onClick={playPs2Click}
            className={({ isActive }) =>
              `px-2 sm:px-3 py-1.5 rounded-md text-[11px] sm:text-xs font-mono transition-colors ${
                isActive
                  ? ps2Mode ? 'text-cyan-300 bg-cyan-500/20 border border-cyan-500/30 font-bold' : 'text-white bg-white/[0.06]'
                  : 'text-white/40 hover:text-white/70'
              }`
            }
            end
          >
            Clubhouse
          </NavLink>
          <NavLink
            to="/events"
            onClick={playPs2Click}
            className={({ isActive }) =>
              `px-2 sm:px-3 py-1.5 rounded-md text-[11px] sm:text-xs font-mono transition-colors ${
                isActive
                  ? ps2Mode ? 'text-cyan-300 bg-cyan-500/20 border border-cyan-500/30 font-bold' : 'text-white bg-white/[0.06]'
                  : 'text-white/40 hover:text-white/70'
              }`
            }
          >
            Events
          </NavLink>
          <NavLink
            to="/join"
            onClick={playPs2Click}
            className={({ isActive }) =>
              `px-2 sm:px-3 py-1.5 rounded-md text-[11px] sm:text-xs font-mono transition-colors ${
                isActive
                  ? ps2Mode ? 'text-cyan-300 bg-cyan-500/20 border border-cyan-500/30 font-bold' : 'text-white bg-white/[0.06]'
                  : 'text-white/40 hover:text-white/70'
              }`
            }
          >
            How to Join
          </NavLink>

          {/* CRT TV Filter Toggle Button */}
          <button
            onClick={() => { toggleCrtFilter(); playPs2Click(); }}
            className={`px-2 py-1 rounded-md text-[10px] font-mono transition-all border flex items-center gap-1.5 ${
              crtFilter
                ? 'bg-blue-600/30 text-blue-200 border-blue-400/50 shadow-[0_0_10px_rgba(59,130,246,0.4)] font-bold'
                : 'bg-white/5 text-white/50 border-white/10 hover:text-white hover:bg-white/10'
            }`}
            title="Toggle PS2 Era CRT TV Scanlines"
          >
            <CrtMonitorIcon className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">CRT</span>
          </button>

          {/* PS2 Theme Toggle Button */}
          <button
            onClick={togglePs2Mode}
            className={`px-2 py-1 rounded-md text-[10px] font-mono transition-all border flex items-center gap-1.5 ${
              ps2Mode
                ? 'bg-cyan-500/30 text-cyan-200 border-cyan-400/50 shadow-[0_0_10px_rgba(6,182,212,0.4)] font-bold'
                : 'bg-white/5 text-white/50 border-white/10 hover:text-white hover:bg-white/10'
            }`}
            title="Toggle PS2 Emotion Engine Theme"
          >
            <Ps2ControllerIcon className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">PS2</span>
          </button>
        </div>
      </div>
    </nav>
  );
}

function HomePage() {
  return (
    <PageTransition>
      <Hero />
      <ActiveGames />
      <GameCatalog />
    </PageTransition>
  );
}

function DiningPage() {
  return (
    <PageTransition>
      <GolfCourseWidget />
      <ClubhouseDining />
    </PageTransition>
  );
}

function EventsPage() {
  return (
    <PageTransition>
      <Events />
    </PageTransition>
  );
}

function AdminEventsPage() {
  return (
    <PageTransition>
      <AdminGate>
        <AdminPanel />
      </AdminGate>
    </PageTransition>
  );
}

function JoinPage() {
  return (
    <PageTransition>
      <HowToJoin />
    </PageTransition>
  );
}

function NotFoundPage() {
  return (
    <PageTransition>
      <NotFound />
    </PageTransition>
  );
}

function AnimatedRoutes() {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<HomePage />} />
        <Route path="/dining" element={<DiningPage />} />
        <Route path="/events" element={<EventsPage />} />
        <Route path="/join" element={<JoinPage />} />
        <Route path="/admin/events" element={<AdminEventsPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </AnimatePresence>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <PS2GraphicsProvider>
          <BrowserRouter>
            <div className="relative min-h-screen bg-black text-white font-sans">
              <AnimatedBackground />
              <PS2CrtOverlay />
              <PS2BootAnimation />

              <div className="relative z-10 flex flex-col min-h-screen">
                <div className="fixed top-0 left-0 right-0 z-50">
                  <Navigation />
                  <AnnouncementBanner />
                </div>
                <main className="flex-1 w-full pt-[72px]" role="main" id="main-content">
                  <AnimatedRoutes />
                </main>
                <Footer />
              </div>
            </div>
          </BrowserRouter>
        </PS2GraphicsProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}
